import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { IconButton, InputBar, Pagination } from '~/ui';
import PointsTable from './PointsTable/PointsTable';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import FiltersIcon from '~/assets/svg/newSvg/filter.svg?react';
import styles from './PointsBoard.module.scss';
import { useDebounce } from '@uidotdev/usehooks';
import { getPaginate } from '~/utils/getPaginate';
import { paginateEnums } from '~/utils/types/paginateEnums';
import { PointsFilters, PointsFilterValues } from './PointsFilters/PointsFilters';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';
import { PointsPageInfo } from '~/pages/Promotions/PointsPageInfo/PointsPageInfo';

interface SelectedPage {
  selected: number;
}

interface PointsBoardProps {
  promoLoyaltyId?: number;
}

const PointsBoard = ({ promoLoyaltyId }: PointsBoardProps) => {
  const { t } = useTranslation();
  const { value, paginate } = getPaginate(paginateEnums.HISTORY_LOYALTY_PAGINATE);
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ field: '', direction: '' });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<PointsFilterValues>({});
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const params = {
    page,
    perPage: 15,
    query: debouncedSearchTerm,
    sort: sort.direction,
    field: sort.field,
    ...(activeFilters.dates?.length ? { dates: activeFilters.dates } : {}),
    ...(promoLoyaltyId ? { promo_loyalty_id: promoLoyaltyId } : {}),
    ...(activeFilters.companies ? { 'filters[companies][names]': activeFilters.companies.names } : {}),
    ...(activeFilters.accommodation_ids?.length
      ? { 'filters[accommodation_ids]': activeFilters.accommodation_ids }
      : {}),
    ...(activeFilters.promo_loyalty_ids?.length
      ? { 'filters[promo_loyalty_ids]': activeFilters.promo_loyalty_ids }
      : {}),
  };

  const { data: points, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/loyalty-history', { params });
      return data;
    },
    queryKey: ['loyalty-history', params],
    keepPreviousData: true,
  });

  const handleFiltersApply = (filters: PointsFilterValues) => {
    setActiveFilters(filters);
    setPage(1);
  };

  return (
    <Board classNames={styles.board}>
      <PointsPageInfo promoLoyaltyId={promoLoyaltyId} filters={activeFilters} />
      <div className={styles.boardHeading}>
        <InputBar
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          value={searchTerm}
          placeholder={t('search_by_email')}
          classNameInput={styles.input}
          classNameInputWrapper={styles.inputWrapper}
        />
        <div className={styles.headingRight}>
          {!promoLoyaltyId && (
            <IconButton
              className={styles.filtersButton}
              onClick={() => setIsFiltersOpen(prev => !prev)}
            >
              <FiltersIcon />
              <span className={styles.filtersButtonText}>
                {isFiltersOpen ? t('hide_filters') : t('show_filters')}
              </span>
            </IconButton>
          )}
        </div>
      </div>
      {isFiltersOpen && !promoLoyaltyId && (
        <PointsFilters onApply={handleFiltersApply} isLoading={isLoading} />
      )}
      <PageTransition page={page}>
        <PointsTable
          points={points?.data || []}
          setSort={setSort}
          sort={sort}
          hidePromoColumn={!!promoLoyaltyId}
          isLoading={isLoading}
        />
      </PageTransition>
      <div className={styles.pointsTablePagination}>
        <TableAmountPage
          firstRow={points?.from}
          lastRow={points?.to}
          total={points?.total}
        />
        {points && (
          <Pagination
            forcePage={paginate}
            searchParam={value}
            pageCount={points?.last_page}
            onPageChange={(selectedPage: SelectedPage) => {
              setPage(selectedPage.selected + 1);
            }}
          />
        )}
      </div>
    </Board>
  );
};

export default PointsBoard;
